import { useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useFormik } from 'formik';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';
import DeleteIcon from '@mui/icons-material/Delete';

import Dialog from 'components/common/Dialog/Dialog';
import CheckboxRadio from 'components/common/CheckboxRadio/CheckboxRadio';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import ComboBox from 'components/common/ComboBox/ComboBox';
import Dropdown from 'components/common/Dropdown/Dropdown';
import IconButton from 'components/common/IconButton/IconButton';
import Input from 'components/common/Input/Input';

import * as K from 'common/constants';
import { useFacetsNeeded } from 'hooks/useFacetsNeeded';
import { facetInfoState } from 'components/search/facets.state';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { dashboardFilterConfigListState } from 'components/dashboards/dashboards.state';
import { FilterConfigType } from 'components/dashboards/types';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { ADD_DASHBOARD_FILTER, EDIT_DASHBOARD_FILTER, GET_CONFIGV2 } from 'components/dashboards/queries';
import { numericStringCompare } from 'common/util/string';
import ELEMENT, { createTestId } from 'common/element';

import './AddEditPrefilterModal.scss';
import { isNameUnique } from 'common/util/dashboardData';
import { searchParamState } from 'components/common/Managers/RouteStateManager.state';

type AddEditPrefilterModalProps = {
  showModal: boolean;
  handleClose: () => void;
  dashboardFilter?: FilterConfigType;
  dashboardName: string;
  index?: number;
};

enum DASHBOARD_FILTER_TYPES {
  FIELD = 'FIELD',
  CUSTOM = 'CUSTOM',
}

const generateFieldFilterInitialValues = (filterType: DASHBOARD_FILTER_TYPES, filterConfig?: FilterConfigType) => {
  if (filterType === DASHBOARD_FILTER_TYPES.FIELD && !!filterConfig) {
    return {
      facet: filterConfig.facet,
      name: filterConfig.name,
      defaultValue: filterConfig.defaultValue,
    };
  }

  return {
    facet: '',
    name: '',
    defaultValue: '',
  };
};

const generateCustomFilterInitialValues = (filterType: DASHBOARD_FILTER_TYPES, filterConfig?: FilterConfigType) => {
  if (filterType === DASHBOARD_FILTER_TYPES.CUSTOM && !!filterConfig) {
    const menuOptions = filterConfig.labelsAndValues.map((labelValueObj) => {
      const label = Object.keys(labelValueObj || {})?.[0];
      const value = Object.values(labelValueObj || {})?.[0];

      return {
        label,
        value,
      };
    });

    return {
      name: filterConfig.name,
      values: menuOptions,
    };
  }

  return {
    name: '',
    values: [
      { label: '', value: '' },
      { label: '', value: '' },
      { label: '', value: '' },
    ],
  };
};

const AddEditPrefilterModal = ({
  showModal,
  handleClose,
  dashboardName,
  dashboardFilter,
  index,
}: AddEditPrefilterModalProps) => {
  const dashboardFilterConfigList = useRecoilValue(dashboardFilterConfigListState);
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);
  const tabName = useRecoilValue(searchParamState(K.URL_PARAM.DASHBOARD_ACTIVE_TAB));
  const [addDashboardFilter] = useMutation(ADD_DASHBOARD_FILTER, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboardName } }],
  });
  const [editDashboardFilter] = useMutation(EDIT_DASHBOARD_FILTER, {
    refetchQueries: [{ query: GET_CONFIGV2, variables: { name: dashboardName } }],
  });
  const [filterType, setFilterType] = useState(
    dashboardFilter && !dashboardFilter.facet ? DASHBOARD_FILTER_TYPES.CUSTOM : DASHBOARD_FILTER_TYPES.FIELD,
  );
  const fieldParamFormik = useFormik({
    initialValues: generateFieldFilterInitialValues(filterType, dashboardFilter),
    onSubmit: (dashboardFilterInput) => {
      if (!isNameUnique(dashboardFilterConfigList, dashboardFilterInput.name, index)) {
        setPageAlertConfig({
          message: 'Another filter has this name, please choose a different one.',
          variant: PageAlertVariant.ERROR,
          autoHide: true,
        });
        return;
      }

      if (!dashboardFilter) {
        addDashboardFilter({
          variables: {
            dashboardName,
            filter: dashboardFilterInput,
            tabName,
          },
        }).then(handleClose);
      } else {
        editDashboardFilter({
          variables: {
            dashboardName,
            filter: dashboardFilterInput,
            filterIndex: index,
            tabName,
          },
        }).then(handleClose);
      }
    },
  });
  const customParamFormik = useFormik({
    initialValues: generateCustomFilterInitialValues(filterType, dashboardFilter),
    onSubmit: (initialDashboardFilterInput) => {
      if (!isNameUnique(dashboardFilterConfigList, initialDashboardFilterInput.name)) {
        setPageAlertConfig({
          message: 'Another filter has this name, please choose a different one.',
          variant: PageAlertVariant.ERROR,
          autoHide: true,
        });
        return;
      }

      const values = initialDashboardFilterInput.values.filter((menuOption) => menuOption.label && menuOption.value);

      const dashboardFilterInput = {
        ...initialDashboardFilterInput,
        values,
      };

      if (!dashboardFilter) {
        addDashboardFilter({
          variables: {
            dashboardName,
            filter: dashboardFilterInput,
            tabName,
          },
        }).then(handleClose);
      } else {
        editDashboardFilter({
          variables: {
            dashboardName,
            filter: dashboardFilterInput,
            filterIndex: index,
            tabName,
          },
        }).then(handleClose);
      }
    },
  });

  const facetInfo = useRecoilValue(facetInfoState);
  useFacetsNeeded();

  const handleSave = () => {
    if (filterType === DASHBOARD_FILTER_TYPES.FIELD) {
      fieldParamFormik.handleSubmit();
    }

    if (filterType === DASHBOARD_FILTER_TYPES.CUSTOM) {
      customParamFormik.handleSubmit();
    }
  };

  const handleAddMenuOption = () => {
    const newMenuOptions = [...customParamFormik.values.values];
    newMenuOptions.push({ label: '', value: '' });
    customParamFormik.setFieldValue('values', newMenuOptions);
  };

  const handleDeleteMenuOption = (index: number) => {
    const newMenuOptions = [...customParamFormik.values.values];
    newMenuOptions.splice(index, 1);
    customParamFormik.setFieldValue('values', newMenuOptions);
  };

  const parameterTypeRadioGroup = (
    <Form.Group className="AddEditParamModal__ParameterTypeRadioGroup">
      <CheckboxRadio
        type="radio"
        label="Field"
        checked={filterType === DASHBOARD_FILTER_TYPES.FIELD}
        onClick={() => setFilterType(DASHBOARD_FILTER_TYPES.FIELD)}
        testId={ELEMENT.dashboardAddPrefiltersFieldCheckbox}
      />
      <CheckboxRadio
        type="radio"
        label="Custom"
        checked={filterType === DASHBOARD_FILTER_TYPES.CUSTOM}
        onClick={() => setFilterType(DASHBOARD_FILTER_TYPES.CUSTOM)}
        testId={ELEMENT.dashboardAddPrefiltersCustomCheckbox}
      />
    </Form.Group>
  );

  const renderParameterForm = () => {
    if (filterType === DASHBOARD_FILTER_TYPES.FIELD) {
      const facets = Object.keys(facetInfo || {});
      const sortedFacets = [...facets].sort((a, b) => numericStringCompare(a, b));

      const facetValues = facetInfo[fieldParamFormik.values.facet || '']?.values || [];
      const sortedFacetValues = [...facetValues].sort((a, b) => numericStringCompare(a, b));
      const size = 'md' as any; // "md" is an invalid type for Boostrap's control input, however it works and is the correct size for this case

      return (
        <Form className="AddEditParamModal__Form">
          <Form.Group>
            <Row className="AddEditParamModal__FormRow">
              <Col className="AddEditParamModal__FormCol" xs={6}>
                <Form.Label>Name</Form.Label>
                <Input
                  type="text"
                  name="name"
                  value={fieldParamFormik.values.name}
                  onChange={fieldParamFormik.handleChange}
                  testId={ELEMENT.dashboardAddPrefiltersFieldNameInput}
                />
              </Col>

              <Col className="AddEditParamModal__FormCol" xs={6}>
                <Form.Label>Field</Form.Label>
                <ComboBox
                  className="DashboardHeaderParams__Input"
                  value={fieldParamFormik.values.facet || ''}
                  onChange={(facetValue) => fieldParamFormik.setFieldValue('facet', facetValue)}
                  size={size}
                  items={sortedFacets}
                  onEnter={() => {}}
                  onSelect={() => {}}
                  selectOnEnter
                  testId={ELEMENT.dashboardAddPrefiltersField}
                />
              </Col>
            </Row>

            <Row className="AddEditParamModal__FormRow">
              <Col className="AddEditParamModal__FormCol">
                <Form.Label>Default Value</Form.Label>
                <Input
                  type="text"
                  name="defaultValue"
                  value={fieldParamFormik.values.defaultValue}
                  onChange={fieldParamFormik.handleChange}
                  testId={ELEMENT.dashboardAddPrefiltersDefaultValueInput}
                />
              </Col>
            </Row>
          </Form.Group>

          <Form.Group>
            <Form.Label>Preview</Form.Label>

            <div className="AddEditParamModal__Preview">
              {fieldParamFormik.values.facet === '' ? (
                'No Field Selected'
              ) : (
                <Form.Group>
                  <Form.Label>{fieldParamFormik.values.name || fieldParamFormik.values.facet}</Form.Label>
                  <ComboBox
                    className="DashboardHeaderParams__Input"
                    value={''}
                    onChange={() => {}}
                    size={size}
                    items={sortedFacetValues}
                    onEnter={() => {}}
                    onSelect={() => {}}
                    selectOnEnter
                  />
                </Form.Group>
              )}
            </div>
          </Form.Group>
        </Form>
      );
    }

    if (filterType === DASHBOARD_FILTER_TYPES.CUSTOM) {
      const menuOptions = customParamFormik.values.values.map((menuOption, index) => (
        <Row className="AddEditParamModal__FormRow AddEditParamModal__MenuOption" key={`${index}`}>
          <Col className="AddEditParamModal__FormCol" xs={4}>
            <Form.Control
              type="text"
              name={`values[${index}].label`}
              value={customParamFormik.values.values[index].label}
              onChange={customParamFormik.handleChange}
              data-test-id={createTestId(ELEMENT.dashboardAddPrefiltersMenuItemLabel, index)}
            />
          </Col>
          <Col className="AddEditParamModal__FormCol" xs={7}>
            <Form.Control
              type="text"
              name={`values[${index}].value`}
              value={customParamFormik.values.values[index].value}
              onChange={customParamFormik.handleChange}
              data-test-id={createTestId(ELEMENT.dashboardAddPrefiltersMenuItemValue, index)}
            />
          </Col>
          <Col xs={1} className="AddEditParamModal__IconContainer">
            <IconButton
              icon={<DeleteIcon fontSize="small" />}
              tooltip="Delete"
              onClick={() => handleDeleteMenuOption(index)}
            />
          </Col>
        </Row>
      ));

      const dropdownSection = customParamFormik.values.values.map((option) => ({
        text: option.label || option.value,
        clickHandler: () => {},
      }));

      return (
        <Form className="AddEditParamModal__Form">
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={customParamFormik.values.name}
              onChange={customParamFormik.handleChange}
              data-test-id={ELEMENT.dashboardAddPrefiltersCustomNameInput}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Menu Options</Form.Label>
            <Row className="AddEditParamModal__FormRow">
              <Col className="AddEditParamModal__FormCol" xs={4}>
                <Form.Label className="AddEditParamModal__MenuOptionLabel">Option Label</Form.Label>
              </Col>
              <Col className="AddCustomFilterModal__FormCol" xs={8}>
                <Form.Label className="AddEditParamModal__MenuOptionLabel">Value</Form.Label>
              </Col>
            </Row>
            {menuOptions}
          </Form.Group>

          <Form.Group>
            <Button
              onClick={handleAddMenuOption}
              variant={ButtonVariant.SECONDARY}
              testId={ELEMENT.dashboardAddPrefiltersCustomAddOption}
            >
              Add Option
            </Button>
          </Form.Group>

          <Form.Group>
            <Form.Label>Preview</Form.Label>
            <div className="AddEditParamModal__Preview">
              {!customParamFormik.values.name ? (
                'No Label Defined'
              ) : (
                <Form.Group>
                  <Form.Label>{customParamFormik.values.name}</Form.Label>
                  <Dropdown sections={[dropdownSection]} size="med">
                    {dropdownSection?.[0]?.text || ''}
                  </Dropdown>
                </Form.Group>
              )}
            </div>
          </Form.Group>
        </Form>
      );
    }

    return null;
  };

  const isAddForm = !dashboardFilter;

  return (
    <Dialog
      className="AddEditParamModal__Dialog"
      title={isAddForm ? 'Add Filter' : 'Edit Filter'}
      show={showModal}
      close={handleClose}
      okButtonText="Save"
      cancelButtonText="Cancel"
      closeBeforeActions={false}
      okCallback={handleSave}
      cancelCallback={handleClose}
      testId={ELEMENT.dashboardAddPrefilters}
    >
      <div className="AddEditParamModal">
        {parameterTypeRadioGroup}
        {renderParameterForm()}
      </div>
    </Dialog>
  );
};

export default AddEditPrefilterModal;
